
import axios from 'axios';
import HelperMixin from '../../faculty-management/employee-table/HelperMixin';
import fileDownload from 'js-file-download';

export default {
  name: 'EmployeeTable',
  mixins: [HelperMixin],
  props: {
    apiUrl: {type: String, required: true},
    downloadUrl: {type: String, required: true},
  },
  data() {
    return {
      filters: {},
      fields: [],
      showInactive: false,
      employees: [],
      employeesSelected: [],
      isLoading: false,
      isExporting: false,
      perPage: 100,
      currentPage: 1,
      showAllColumns: false,
      deletePermission: false,
      columnsAddedWhileFilterActive: [],
    };
  },
  computed: {
    visibleFields() {
      return this.fields.filter((field) => field.visible);
    },
    selectableFields() {
      // columns checkbox, edit and name should not be able to be hidden
      const fieldNames = ['checkbox', 'edit', 'name'];
      return this.fields.filter((field) => !fieldNames.includes(field.key));
    },
    filteredEmployees() {
      return this.employees.filter((employee) =>
        Object.keys(this.filters).every((key) =>
          this.matchesFilter(employee, key, this.filters[key]),
        ),
      );
    },
    totalRows() {
      return this.filteredEmployees.length;
    },
    combinedMailAddresses() {
      const collection = this.employeesSelected.length ? this.employeesSelected : this.filteredEmployees;
      return collection
          .map((employee) => employee['emailWWU'])
          .filter(Boolean);
    },
  },
  watch: {
    'visibleFields'(newValue, oldValue) {
      const checkbox = $('#columnCheckbox');
      if (this.fields.find((field) => !field.visible) !== undefined) {
        this.showAllColumns = false;
        checkbox.prop('checked', false);
      } else {
        this.showAllColumns = true;
        checkbox.prop('checked', true);
      }
      window.localStorage.setItem('employeeTableShowAllColumns', this.showAllColumns);
      if (!this.showAllColumns) {
        const visibility = this.fields.reduce((result, column) => ({...result, [column.key]: column.visible}), {});
        window.localStorage.setItem('employeeTableColumnSettings', JSON.stringify(visibility));
      }
      // lock width for newly added columns
      if (newValue.length > oldValue.length) {
        this.lockColumnWidths();
        // store newly added columns if filters are set
        if (this.filteredEmployees.length < this.employees.length) {
          const oldKeys = oldValue.map(field => field.key);
          this.columnsAddedWhileFilterActive = newValue.map(f => f.key).filter(key => !oldKeys.includes(key));
        }
      }
      // calculate offset values for non-overlapping sticky columns
      this.computeStickyColumnOffsets();
    },
    'filteredEmployees'(newValue, oldValue) {
      this.computeStickyColumnOffsets();

      // if columns were added while filters were set and filter result gets broader
      if (this.columnsAddedWhileFilterActive.length && newValue.length > oldValue.length) {
        // reset width of stored columns so their width will be calculated again
        for (const key of this.columnsAddedWhileFilterActive) {
          $(`th.col-${key}`).width('');
        }
        this.lockColumnWidths();
        // reset stored columns if all filters were cleared
        if (newValue.length === this.employees.length) {
          this.columnsAddedWhileFilterActive = [];
        }
      }
    },
    'filters.emailWWU.value'() {
      this.dynamicColumnWidth('emailWWU');
    },
    'filters.emailPrivate.value'() {
      this.dynamicColumnWidth('emailPrivate');
    },
  },
  mounted() {
    this.fetchData();
    this.calculateContainerHeight();
    window.addEventListener('resize', this.calculateContainerHeight);
    window.addEventListener('resize', this.computeStickyColumnOffsets);
  },
  methods: {
    matchesFilter(employee, key, filter) {
      if (filter.type === 'string-contains') {
        if (filter.value === '') return true;
        if (filter.value === '---') {
          const val = employee[key];
          return val === null || val === undefined || val === '';
        }
        const field = this.fields.find((f) => f.key === key);
        if (field.filterByFormatted) {
          return String(field.formatter({value: employee[key]})).includes(filter.value.toString());
        }
        return String(employee[key]).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
            .includes(filter.value.toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, ''));
      }
      if (filter.type === 'string-equals') {
        if (filter.value === '') return true;
        if (filter.value === '---') {
          const val = employee[key];
          return val === null || val === undefined || val === '';
        }
        const field = this.fields.find((f) => f.key === key);
        if (field.filterByFormatted) {
          return String(field.formatter({value: employee[key]})) === filter.value.toString();
        }
        return String(employee[key]).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '') ===
            filter.value.toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
      }
      if (filter.type === 'multiselect') { // multiselect fields must not use filteredByFormatted option
        if (filter.value.length === 0) return true;
        return filter.value.some((selected) => {
          if (selected.id === '__empty__') {
            const val = employee[key];
            const containsEmptyValue = function (target) {
              if (Array.isArray(target)) return target.length === 0 || target.some(item => containsEmptyValue(item));
              else return target === null || target === undefined || target === '';
            }
            return containsEmptyValue(val);
          }

          const deepIncludes = function(target, value) { // recursive includes for arrays
            if (target === value) return true;
            if (Array.isArray(target)) {
              for (const elem of target) {
                if (deepIncludes(elem, value)) return true;
              }
            }
            return false;
          }

          return deepIncludes(employee[key], selected.id);
        });
      }
      return true;
    },
    fetchData() {
      this.isLoading = true;
      this.employeesSelected = [];
      axios.get(this.apiUrl, {
        params: {
          showInactive: this.showInactive,
        },
      }).then((response) => {
        const columns = response.data.columns;
        const columnNames = Object.keys(columns);

        // build filter specifications
        const allFilterSpecs = columnNames.reduce((specs, name) => {
          if (typeof columns[name].filter !== 'undefined') {
            specs[name] = columns[name].filter;
          }
          return specs;
        }, {});

        // build column specifications
        const allColumnSpecs = columnNames.map((key) => {
          const columnSpec = {
            key: key,
            label: columns[key].label,
            title: columns[key].title,
            sortable: columns[key].sortable,
            visible: columns[key].visible,
            class: 'col-' + key + ' table-b-table-default',
          };
          if (typeof columns[key].formatter !== 'undefined') {
            columnSpec['formatter'] = this[columns[key].formatter];
            if (typeof columns[key].sortByFormatted !== 'undefined') {
              columnSpec['sortByFormatted'] = columns[key].sortByFormatted;
            }
            if (typeof columns[key].filterByFormatted !== 'undefined') {
              columnSpec['filterByFormatted'] = columns[key].filterByFormatted;
            }
          }
          if (typeof columns[key].stickyColumn !== 'undefined') {
            columnSpec['stickyColumn'] = columns[key].stickyColumn;
          }
          if (typeof columns[key].class !== 'undefined') {
            columnSpec['class'] += ' ' + columns[key].class;
          }
          return columnSpec;
        });

        // add "---" empty option to all multiselect filters
        Object.values(allFilterSpecs).forEach((filter) => {
          if (filter.type === 'multiselect') {
            filter.options = [{id: '__empty__', name: '---'}, ...(filter.options || [])];
            filter.dynamicOptions = filter.options;
          }
        });

        // update data
        this.filters = allFilterSpecs;
        this.fields = allColumnSpecs;
        this.employees = Object.freeze(response.data.employees);
        this.deletePermission = response.data.deletePermission;
        this.isLoading = false;

        // restore hidden columns
        const showAllColumns = JSON.parse(window.localStorage.getItem('employeeTableShowAllColumns'));
        const visibilityDict = JSON.parse(window.localStorage.getItem('employeeTableColumnSettings'));
        if (!showAllColumns && visibilityDict) {
          Object.keys(visibilityDict).forEach((column) => {
            const field = this.fields.find((field) => field.key === column);
            if (field) {
              field.visible = visibilityDict[column];
            }
          });
        } else if (showAllColumns) {
          this.selectableFields.forEach((field, index, fields) => fields[index].visible = true);
        }
      }).catch(() => {
        this.isLoading = false;
      });
    },
    calculateDynamicFilterOptions(key) {
      const filteredOptionIds = new Set();
      const originalOptionIds = this.filters[key].options.map((opt) => opt.id);

      // if filter of field has no option selected, iterate over filtered employees, else over all
      for (const employee of (!this.filters[key].value.length && this.filteredEmployees || this.employees)) {
        const cellValue = employee[key];
        const cellOptionIds = [];

        // get corresponding multiselect option(s) for current cell value
        const pushOption = function (value) {
          if (Array.isArray(value)) {
            if (value.length === 0) cellOptionIds.push('__empty__')
            else value.forEach(item => pushOption(item))
          } else if (value === null || value === undefined || value === '') cellOptionIds.push('__empty__')
          else cellOptionIds.push(value);
        }
        pushOption(cellValue);

        // check if new option(s) is/are already contained in filtered options
        const newOptionIds = cellOptionIds.filter(optId => !filteredOptionIds.has(optId));
        if (!newOptionIds.length) continue;

        // check if value would pass other filters without filter of current column
        if (this.filters[key].value.length) {
          const passesOthers = Object.keys(this.filters).every((otherKey) =>
              otherKey === key || this.matchesFilter(employee, otherKey, this.filters[otherKey]),
          );
          if (!passesOthers) continue;
        }

        // add new options to filtered options
        for (const optId of newOptionIds) {
          filteredOptionIds.add(optId);
        }

        // break if all options contained
        if (filteredOptionIds.size === originalOptionIds.length) {
          break;
        }
      }

      // set new dynamic options
      this.filters[key].dynamicOptions = this.filters[key].options.filter((opt) => filteredOptionIds.has(opt.id));
    },
    onRowSelected(rows) {
      this.employeesSelected = rows;
    },
    toggleInactive() {
      this.showInactive = !this.showInactive;
      this.fetchData();
    },
    toggleSelection(index) {
      if (this.$refs.table.selection.isActivated(index)) {
        this.$refs.table.selection.remove(index);
      } else {
        this.$refs.table.selection.add(index);
      }
    },
    resetMultiselect(filter) {
      filter.value = [];
    },
    downloadEmployees() {
      let idList = [];
      this.isExporting = true;
      if (this.employeesSelected.length === 0) {
        idList = this.filteredEmployees.map((employee) => employee.id);
      } else {
        idList = this.employeesSelected.map((employee) => employee.id);
      }
      axios({
        url: this.downloadUrl,
        method: 'POST',
        responseType: 'blob',
        data: {
          employees: idList,
          columns: this.visibleFields.map((field) => field.key),
        },
      }).then((response) => {
        fileDownload(response.data, 'Beschäftigte.xlsx');
        this.isExporting = false;
      }).catch(() => {
        this.isExporting = false;
      });
    },
    clearFilter(key) {
      this.filters[key].value = '';
    },
    clearFilters() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key].value = '';
      });
    },
    calculateContainerHeight() {
      const table = $(this.$refs['table'].$el);
      const windowHeight = $(window).height();
      const offsetTop = table.offset().top;
      const footerHeight = $('footer').outerHeight();
      const tableHeight = windowHeight - offsetTop - footerHeight - 17;
      table.parents('.table-container').height(tableHeight);
    },
    copyAddressToClipboard(address) {
      navigator.clipboard.writeText(address.replaceAll('<br>', '\n'));
    },
    getLabel(key) {
      return this.fields.find((field) => field.key === key).label;
    },
    resetVisibleFields() {
      const checkbox = $('#columnCheckbox');
      if (checkbox.prop('checked')) {
        this.selectableFields.forEach((field, index, fields) => fields[index].visible = true);
      } else {
        const visibility = JSON.parse(window.localStorage.getItem('employeeTableColumnSettings'));
        if (visibility) {
          Object.keys(visibility).forEach((column) => {
            const field = this.fields.find((e) => e.key === column);
            // note that the field may currently be hidden, i.e. it must not be present in the table
            if (field) {
              field.visible = visibility[column];
            }
          });
        }
      }
    },
    computeStickyColumnOffsets() {
      // wait until tables cells have been rendered
      this.$nextTick(() => {
        let offset = 0;
        // get header cells
        $('th.non-stacking-sticky-column').each(function() {
          // set header css
          $(this).css({'left': offset + 'px', 'z-index': 5});
          // get corresponding td cells
          const className = $(this).attr('class').split(' ').filter((className) => (className.startsWith('col-')));
          const cells = $('td.'+className);
          // set td cells css 'position' : '-webkit-sticky'
          cells.css({'left': offset + 'px', 'z-index': 2, 'position': 'sticky'});
          cells.css('position', '-webkit-sticky');
          // increase offset
          offset += cells.outerWidth();
        });
      });
    },
    dynamicColumnWidth(key) { // for columns with dynamic width
      this.$nextTick(() => {
        const th = $('th.col-' + key).width('');
        const maxWidth = Math.max(th[0].scrollWidth, ...$('td.col-' + key).toArray().map((td) => td.scrollWidth));
        th.width(maxWidth);
      });
    },
    lockColumnWidths() { // set fixed widths on columns to prevent jumps on filtering
      this.$nextTick(function() {
        $('th').filter((_, th) => th.style.width === '').each(function () {
          const colClass = $(this).attr('class').split(' ').find((className) => (className.startsWith('col-')));
          const maxWidth = Math.max(this.scrollWidth, ...$(`td.${colClass}`).toArray().map((td) => td.scrollWidth));
          $(this).width(maxWidth);
        });
      });
    },
  },
};
